import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axiosInstance from "../api/axiosInstance";
import { useAuth } from "../context/AuthContext";
import {
  Table, Card, Button, Space, Typography, Tag, Badge, Modal,
  notification, Breadcrumb, Alert, Input, Form, Descriptions,
  Select, Popconfirm, Divider, List
} from 'antd';
import {
  FaTools, FaCheck, FaSyncAlt, FaClock, FaExclamationTriangle,
  FaCheckCircle, FaTimes, FaUserCog, FaInfoCircle
} from 'react-icons/fa';
import './RoomMaintenancePage.css';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const RoomMaintenancePage = () => {
  const { authData } = useAuth();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentTask, setCurrentTask] = useState(null);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [staff, setStaff] = useState([]);
  const [assignForm] = Form.useForm();
  const [apiErrors, setApiErrors] = useState(null);
  const [filterStatus, setFilterStatus] = useState('All');
  const [showInfoModal, setShowInfoModal] = useState(false);

  const breadcrumbItems = [
    {
      title: <Link to={authData?.role?.toLowerCase() === 'systemadmin' ? '/admin/dashboard' : '/hotels/dashboard'}>Home</Link>,
    },
    {
      title: 'Room Maintenance',
    },
  ];

  const statusOptions = [
    { value: 'Pending', label: 'Pending', color: 'orange', icon: <FaClock /> },
    { value: 'InProgress', label: 'In Progress', color: 'blue', icon: <FaTools /> },
    { value: 'Completed', label: 'Completed', color: 'green', icon: <FaCheckCircle /> },
    { value: 'Cancelled', label: 'Cancelled', color: 'red', icon: <FaTimes /> },
  ];

  const priorityOptions = [
    { value: 'Low', label: 'Low', color: 'green' },
    { value: 'Medium', label: 'Medium', color: 'orange' },
    { value: 'High', label: 'High', color: 'red' },
    { value: 'Emergency', label: 'Emergency', color: 'volcano' },
  ];

  useEffect(() => {
    if (authData?.hotelId) {
      fetchTasks();
      fetchMaintenanceStaff();
    }
  }, [authData?.hotelId, filterStatus]);

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`/Maintenance/GetAllMaintenanceTasks?hotelId=${authData.hotelId}`);
      let filteredTasks = response.data || [];
      
      if (filterStatus !== 'All') {
        filteredTasks = filteredTasks.filter(task => task.status === filterStatus);
      }

      setTasks(filteredTasks.map(task => ({
        ...task,
        key: task.maintenanceTaskId,
      })));
      setApiErrors(null);
    } catch (err) {
      console.error("Error fetching tasks:", err);
      setApiErrors(err.response?.data);
      notification.error({
        message: "Error",
        description: err.response?.data?.message || "Failed to load maintenance tasks"
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchMaintenanceStaff = async () => {
    try {
      const response = await axiosInstance.get("/User/WithPermission", { 
        params: { 
          hotelId: authData.hotelId,
          permission: "MaintenanceAssignment" 
        } 
      });
      setStaff(response.data.users || []);
    } catch (err) {
      console.error("Error fetching staff:", err);
      notification.error({
        message: "Error",
        description: err.response?.data?.message || "Failed to load maintenance staff"
      });
    }
  };

  const handleAssignTask = async (values) => {
    try {
      const response = await axiosInstance.post('/Maintenance/Assign', {
        ...values,
        maintenanceTaskId: currentTask.maintenanceTaskId,
        hotelId: authData.hotelId
      });
      
      // Update the task in state with the assigned user
      const updatedTasks = tasks.map(task => 
        task.maintenanceTaskId === currentTask.maintenanceTaskId
          ? {
              ...task,
              assignedTo: staff.find(user => user.userId === values.userId),
              assignedToUserId: values.userId,
              assignmentNotes: values.assignmentNotes
            }
          : task
      );
      
      setTasks(updatedTasks);
      notification.success({
        message: 'Success',
        description: 'Task assigned successfully'
      });
      setAssignModalOpen(false);
    } catch (err) {
      console.error("Error assigning task:", err);
      setApiErrors(err.response?.data);
      notification.error({
        message: "Error",
        description: err.response?.data?.message || "Failed to assign task"
      });
    }
  };

 const handleCompleteTask = async (taskId) => {
  try {
    const taskToComplete = tasks.find(t => t.maintenanceTaskId === taskId);
    
    if (!taskToComplete) {
      notification.error({
        message: 'Error',
        description: 'Task not found in local state'
      });
      return;
    }

    // First complete the task
    const response = await axiosInstance.put(`/Maintenance/CompleteTask/${taskId}`, {
      hotelId: authData.hotelId  // Make sure this is included
    });

    if (response.status === 200) {
      notification.success({
        message: 'Success',
        description: 'Task marked as completed'
      });

      // Then update room status if applicable
      if (taskToComplete.roomId) {
        try {
          await axiosInstance.put(`/Rooms/UpdateStatus/${taskToComplete.roomId}`, {
            status: 'Available',
            hotelId: authData.hotelId
          });
        } catch (roomError) {
          console.error("Room status update failed:", roomError);
          notification.warning({
            message: 'Note',
            description: 'Task completed but room status could not be updated'
          });
        }
      }

      // Refresh the task list
      fetchTasks();
    }
  } catch (err) {
    console.error("Error completing task:", err);
    let errorMessage = "Failed to complete task";
    
    if (err.response) {
      if (err.response.status === 404) {
        errorMessage = "Task not found - it may have been deleted";
      } else if (err.response.data?.message) {
        errorMessage = err.response.data.message;
      }
    }

    notification.error({
      message: "Error",
      description: errorMessage
    });
  }
};
  const renderStatusTag = (status) => {
    const config = statusOptions.find(opt => opt.value === status) || {};
    return (
      <Tag color={config.color} icon={config.icon}>
        {config.label || status}
      </Tag>
    );
  };

  const renderPriorityTag = (priority) => {
    const config = priorityOptions.find(opt => opt.value === priority) || {};
    return (
      <Tag color={config.color}>
        {config.label || priority}
      </Tag>
    );
  };

  const columns = [
    {
      title: 'Task ID',
      dataIndex: 'maintenanceTaskId',
      key: 'maintenanceTaskId',
      width: 100,
      sorter: (a, b) => a.maintenanceTaskId - b.maintenanceTaskId
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: 'Room',
      dataIndex: 'roomNumber',
      key: 'roomNumber',
      render: (text, record) => text || (record.roomId ? `ID: ${record.roomId}` : record.location || 'N/A')
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      filters: statusOptions.map(opt => ({ text: opt.label, value: opt.value })),
      render: renderStatusTag,
      onFilter: (value, record) => record.status === value,
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      render: renderPriorityTag
    },
    {
      title: 'Assigned To',
      key: 'assignedTo',
      render: (_, record) => {
        if (record.assignedTo) {
          return (
            <div>
              <Text>{record.assignedTo.userName}</Text>
              <br />
              <Text type="secondary">{record.assignedTo.role}</Text>
            </div>
          );
        }
        return <Text type="secondary">Not assigned</Text>;
      }
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Button
            icon={<FaCheck />}
            onClick={() => handleCompleteTask(record.maintenanceTaskId)}
            disabled={record.status === 'Completed'}
          >
            Complete
          </Button>
          <Button
            icon={<FaUserCog />}
            onClick={() => {
              setCurrentTask(record);
              setAssignModalOpen(true);
            }}
          >
            Assign
          </Button>
        </Space>
      )
    }
  ];

  const InfoModal = () => (
    <Modal
      title="Room Maintenance Tracking"
      open={showInfoModal}
      onCancel={() => setShowInfoModal(false)}
      footer={[
        <Button key="close" onClick={() => setShowInfoModal(false)}>
          Close
        </Button>
      ]}
      width={700}
    >
      <p>This module allows you to track and manage room maintenance progress.</p>
      
      <h4><FaTools /> Key Features:</h4>
      <ul>
        <li>View all active maintenance tasks for rooms</li>
        <li>Assign staff members to maintenance tasks</li>
        <li>Update task status (mark as completed)</li>
        <li>Filter tasks by status</li>
      </ul>

      <h4><FaClock /> Status Indicators:</h4>
      <List
        size="small"
        dataSource={statusOptions}
        renderItem={item => (
          <List.Item>
            <Tag color={item.color} icon={item.icon}>
              {item.label}
            </Tag>
            {item.value === 'Pending' && 'Task has been created but not started'}
            {item.value === 'InProgress' && 'Task is currently being worked on'}
            {item.value === 'Completed' && 'Task has been finished'}
            {item.value === 'Cancelled' && 'Task was cancelled'}
          </List.Item>
        )}
      />

      <h4><FaExclamationTriangle /> Priority Levels:</h4>
      <List
        size="small"
        dataSource={priorityOptions}
        renderItem={item => (
          <List.Item>
            <Tag color={item.color}>
              {item.label}
            </Tag>
            {item.value === 'Low' && 'Minor issues that don\'t affect room usability'}
            {item.value === 'Medium' && 'Issues that affect guest experience'}
            {item.value === 'High' && 'Critical issues affecting room functionality'}
            {item.value === 'Emergency' && 'Immediate attention required (safety issues)'}
          </List.Item>
        )}
      />

      <Divider />

      <h4>How To Use:</h4>
      <ol>
        <li>Use the status filter to view specific types of tasks</li>
        <li>Click "Assign" to assign a staff member to a task</li>
        <li>Click "Complete" when a task is finished</li>
        <li>Use the refresh button to get the latest updates</li>
      </ol>
    </Modal>
  );

  return (
    <div className="room-maintenance-page">
      <Breadcrumb items={breadcrumbItems} style={{ marginBottom: 16 }} />

      <Card
        title={
          <Space>
            <FaTools style={{ fontSize: '18px', marginRight: '8px' }} />
            <Title level={4} style={{ margin: 0 }}>Room Maintenance</Title>
            <Badge count={tasks.length} showZero style={{ backgroundColor: '#1890ff' }} />
            <Button 
              type="text" 
              icon={<FaInfoCircle />} 
              onClick={() => setShowInfoModal(true)}
            />
          </Space>
        }
        extra={
          <Space>
            <Select
              defaultValue="All"
              style={{ width: 120 }}
              onChange={setFilterStatus}
              options={[
                { value: 'All', label: 'All Statuses' },
                ...statusOptions.map(opt => ({ value: opt.value, label: opt.label }))
              ]}
            />
            <Button icon={<FaSyncAlt />} onClick={fetchTasks} loading={loading}>
              Refresh
            </Button>
          </Space>
        }
      >
        {apiErrors && (
          <Alert
            message="Error"
            description={apiErrors.message || 'An error occurred'}
            type="error"
            showIcon
            closable
            style={{ marginBottom: 16 }}
          />
        )}

        <Table
          columns={columns}
          dataSource={tasks}
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50'],
            showTotal: (total) => `Total ${total} tasks`
          }}
          scroll={{ x: true }}
        />

        {/* Assign Task Modal */}
        <Modal
          title={`Assign Task #${currentTask?.maintenanceTaskId || ''}`}
          open={assignModalOpen}
          onCancel={() => setAssignModalOpen(false)}
          footer={null}
          destroyOnClose
        >
          <Form
            form={assignForm}
            layout="vertical"
            onFinish={handleAssignTask}
            initialValues={{
              userId: currentTask?.assignedToUserId || null,
              assignmentNotes: currentTask?.assignmentNotes || ''
            }}
          >
            <Form.Item
              label="Assign To"
              name="userId"
              rules={[{ required: true, message: 'Please select a staff member' }]}
            >
              <Select 
                placeholder={staff.length === 0 ? "No staff available" : "Select maintenance staff"}
                disabled={staff.length === 0}
              >
                {staff.map(user => (
                  <Option key={user.userId} value={user.userId}>
                    {user.userName} ({user.role})
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="Assignment Notes"
              name="assignmentNotes"
            >
              <TextArea rows={3} placeholder="Any special instructions..." />
            </Form.Item>

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit">
                  Assign Task
                </Button>
                <Button onClick={() => setAssignModalOpen(false)}>Cancel</Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
      </Card>

      {/* Info Modal */}
      <InfoModal />
    </div>
  );
};

export default RoomMaintenancePage;